import React, { useState, useEffect } from 'react';
import {
  adminMetricsService,
  ActiveUsersMetric,
  AvgAdherenceMetric,
  UsersWithCustomRemindersMetric,
  AvgChatMessagesMetric,
  MealRecordPhotoVsDescriptionMetric,
  ActiveUsersWithNotificationsMetric,
} from '../../services/adminMetricsService';
import MetricCard from './MetricCard';
import { UsersIcon, ActivityIcon, BellAltIcon, ChatIcon, ImageIcon, BellRingIcon } from '../../assets/icons';

interface AdminMetricsProps {
  totalPatients: number;
}

const AdminMetrics: React.FC<AdminMetricsProps> = ({ totalPatients }) => {
  const [activeUsers, setActiveUsers] = useState<ActiveUsersMetric | null>(null);
  const [avgAdherence, setAvgAdherence] = useState<AvgAdherenceMetric | null>(null);
  const [customReminders, setCustomReminders] = useState<UsersWithCustomRemindersMetric | null>(null);
  const [avgChatMessages, setAvgChatMessages] = useState<AvgChatMessagesMetric | null>(null);
  const [mealRecordPhotoVsDescription, setMealRecordPhotoVsDescription] = useState<MealRecordPhotoVsDescriptionMetric | null>(null);
  const [activeUsersWithNotifications, setActiveUsersWithNotifications] = useState<ActiveUsersWithNotificationsMetric | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const [
          activeUsersData,
          avgAdherenceData,
          customRemindersData,
          avgChatMessagesData,
          mealRecordData,
          activeUsersWithNotificationsData,
        ] = await Promise.all([
          adminMetricsService.getActiveUsers(),
          adminMetricsService.getAvgAdherence(),
          adminMetricsService.getUsersWithCustomReminders(),
          adminMetricsService.getAvgChatMessages(),
          adminMetricsService.getMealRecordPhotoVsDescription(),
          adminMetricsService.getActiveUsersWithNotifications(),
        ]);
        setActiveUsers(activeUsersData);
        setAvgAdherence(avgAdherenceData);
        setCustomReminders(customRemindersData);
        setAvgChatMessages(avgChatMessagesData);
        setMealRecordPhotoVsDescription(mealRecordData);
        setActiveUsersWithNotifications(activeUsersWithNotificationsData);
      } catch (err) {
        console.error('Failed to fetch admin metrics:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatSinceDate = (since: string) => {
    const date = new Date(since);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginTop: 0, marginBottom: 20 }}>
        Métricas de Administración
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 20,
        }}
      >
        <MetricCard
          title="Total Pacientes"
          iconBg="#f0fdfa"
          iconColor="#38b2ac"
          icon={<UsersIcon />}
          value={<span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>{totalPatients}</span>}
        />

        <MetricCard
          title="Usuarios Activos"
          iconBg="#eff6ff"
          iconColor="#3b82f6"
          icon={<UsersIcon />}
          loading={loading}
          error={error}
          subtitle="Usuarios que han registrado al menos una acción en la agenda en la última semana"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {activeUsers?.activeUsers}
              </span>
              {activeUsers?.since && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  desde {formatSinceDate(activeUsers.since)}
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Adherencia Media"
          iconBg="#faf5ff"
          iconColor="#8b5cf6"
          icon={<ActivityIcon />}
          loading={loading}
          error={error}
          subtitle="Adherencia media de los usuarios activos en los ultimos 3 meses"
          value={
            <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
              {avgAdherence?.avgAdherence != null ? `${avgAdherence.avgAdherence.toFixed(1)}%` : '-'}
            </span>
          }
        />

        <MetricCard
          title="Recordatorios Personalizados"
          iconBg="#fff7ed"
          iconColor="#f97316"
          icon={<BellAltIcon />}
          loading={loading}
          error={error}
          subtitle="Usuarios que han configurado al menos un recordatorio personalizado"
          value={
            <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
              {customReminders?.usersWithCustomReminders ?? '-'}
            </span>
          }
        />

        <MetricCard
          title="Mensajes de Chat (Media)"
          iconBg="#f0fdf4"
          iconColor="#22c55e"
          icon={<ChatIcon />}
          loading={loading}
          error={error}
          subtitle="Media de mensajes de chat por usuario activo"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {avgChatMessages?.avgChatMessages != null ? avgChatMessages.avgChatMessages.toFixed(1) : '-'}
              </span>
              {avgChatMessages?.since && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  desde {formatSinceDate(avgChatMessages.since)}
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Registros con Foto"
          iconBg="#fdf4ff"
          iconColor="#a855f7"
          icon={<ImageIcon />}
          loading={loading}
          error={error}
          subtitle="Porcentaje de registros de comida que incluyen foto"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {mealRecordPhotoVsDescription?.photoPercentage != null
                  ? mealRecordPhotoVsDescription.photoPercentage.toFixed(1)
                  : '-'}%
              </span>
              {mealRecordPhotoVsDescription?.total != null && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  de {mealRecordPhotoVsDescription.total} registros
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Activos con Notificaciones"
          iconBg="#eff6ff"
          iconColor="#6366f1"
          icon={<BellRingIcon />}
          loading={loading}
          error={error}
          subtitle="Usuarios activos que tienen notificaciones habilitadas"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {activeUsersWithNotifications?.percentage != null
                  ? activeUsersWithNotifications.percentage.toFixed(1)
                  : '-'}%
              </span>
              {activeUsersWithNotifications?.withNotifications != null && activeUsersWithNotifications?.activeUsers != null && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  {activeUsersWithNotifications.withNotifications}/{activeUsersWithNotifications.activeUsers} usuarios
                </span>
              )}
            </>
          }
        />
      </div>
    </div>
  );
};

export default AdminMetrics;
